import React, { useState, useEffect, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { uploadData, getUrl } from 'aws-amplify/storage'; 
import { listBooks, listCourses } from '../graphql/queries';
import { createBook, createCourse, updateBook, updateCourse, deleteBook, deleteCourse } from '../graphql/mutations';
import { FiPackage, FiTrash2, FiVideo, FiImage, FiFileText, FiCheckCircle, FiEdit, FiUser } from 'react-icons/fi';

const InventoryManager = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState('list'); 
    const [editingId, setEditingId] = useState(null);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

    const initialFormState = {
        title: '',
        author: 'Dr. Giovanni Scarvaci',
        priceUSD: '',
        pricePhysical: '',
        pricePDF: '',
        priceOnline: '',
        description: '',
        category: 'LIBRO',
        hasDigital: true,
        hasOnline: false,
        hasPhysical: false,
        imageKey: '', 
        images: [], 
        pdfKey: '',
        videoKey: '',
        onlineExtension: '',
        imagePrefix: '',
        s3FolderName: '',
        totalPages: 0
    };

    const [newProduct, setNewProduct] = useState(initialFormState);

    const fetchInventory = useCallback(async () => {
        const client = generateClient();
        setLoading(true);
        try {
            // Usamos apiKey para lectura pública del catálogo
            const [bookRes, courseRes] = await Promise.all([
                client.graphql({ query: listBooks, authMode: 'apiKey' }),
                client.graphql({ query: listCourses, authMode: 'apiKey' })
            ]);
            
            const rawProducts = [...bookRes.data.listBooks.items, ...courseRes.data.listCourses.items];
            
            const productsWithImages = await Promise.all(rawProducts.map(async (p) => {
                if (p.imageKey) {
                    try {
                        const urlRes = await getUrl({ 
                            path: `public/${p.imageKey}` 
                        });
                        return { ...p, tempUrl: urlRes.url.toString() };
                    } catch (err) { return p; }
                }
                return p;
            }));

            setProducts(productsWithImages);
        } catch (e) { 
            console.error("Error en fetch:", e); 
        } finally { 
            setLoading(false); 
        }
    }, []);

    useEffect(() => { fetchInventory(); }, [fetchInventory]);

const handleDelete = async (id, category) => {
        if (!window.confirm("¿Estás seguro de eliminar esta obra?")) return;
        const client = generateClient();
        setLoading(true);
        try {
            const mutation = category === 'CURSO' ? deleteCourse : deleteBook;
            const result = await client.graphql({
                query: mutation,
                variables: { input: { id } },
                authMode: 'userPool' // Mantenemos la llave maestra
            });

            if (result.errors) {
                console.error("Errores de GraphQL:", result.errors);
                alert(`Error: ${result.errors[0].message}`);
            } else {
                alert("✅ Eliminado correctamente");
                fetchInventory();
            }
        } catch (error) {
            console.error("Error completo al eliminar:", error);
            // Si el error es un 401/403 de AppSync, vendrá dentro de error.errors
            const msg = error.errors ? error.errors[0].message : "Error de red o permisos.";
            alert(`No se pudo eliminar: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (product) => {
        setNewProduct({
            ...product,
            videoKey: (product.videos && product.videos[0]) || ""
        });
        setEditingId(product.id);
        setView('create');
    };

    const handleFileUpload = async (e, field, isMultiple = false) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        try {
            setLoading(true);
            const keys = [];
            for (const file of files) {
                const fileName = `${Date.now()}-${file.name.replace(/\s/g, '_')}`;
                const key = `catalog/${fileName}`;
                
                // CAMBIO CLAVE: Subida con acceso público para que clientes vean portadas
                await uploadData({
                    path: `public/${key}`,
                    data: file,
                    options: { 
                        contentType: file.type
                    }
                }).result;
                keys.push(key);
            }
            if (isMultiple) {
                setNewProduct(prev => ({ ...prev, images: [...prev.images, ...keys] }));
            } else {
                setNewProduct(prev => ({ ...prev, [field]: keys[0] }));
            }
        } catch (error) {
            console.error("Error S3:", error);
            alert("Error al subir archivo. Revisa permisos de S3.");
        } finally {
            setLoading(false);
        }
    };

    const handleFolderUpload = async (e, bookId) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        
        const imageFiles = files
            .filter(file => file.type.startsWith('image/'))
            .sort((a, b) => a.name.localeCompare(b.name));

        if (imageFiles.length === 0) return alert("No se encontraron imágenes en la carpeta.");

        const firstFile = imageFiles[0];
        const fileName = firstFile.name;
        const extension = fileName.split('.').pop().toLowerCase();
        
        let detectedPrefix = "";
        if (fileName.startsWith("P-")) detectedPrefix = "P-";
        else if (fileName.startsWith("Page_")) detectedPrefix = "Page_";
        else {
            const match = fileName.match(/^([a-zA-Z_-]+)/);
            detectedPrefix = match ? match[1] : "";
        }
        
        setNewProduct(prev => ({ 
            ...prev, 
            onlineExtension: extension,
            imagePrefix: detectedPrefix,
            s3FolderName: bookId,
            totalPages: imageFiles.length
        }));

        setUploadProgress({ current: 0, total: imageFiles.length });
        setLoading(true);

        try {
            for (let i = 0; i < imageFiles.length; i++) {
                const file = imageFiles[i];
                // Ruta protegida dentro de public/books-data/
                const key = `books-data/${bookId}/${file.name}`;
                
                await uploadData({
                    path: `public/${key}`,
                    data: file,
                    options: { contentType: file.type }
                }).result;

                setUploadProgress(prev => ({ ...prev, current: i + 1 }));
            }
            alert(`✅ Carpeta cargada.\nFormato: ${extension}\nPrefijo: ${detectedPrefix}\nTotal: ${imageFiles.length} págs.`);
        } catch (error) {
            alert("Error al subir la carpeta");
        } finally {
            setLoading(false);
            setUploadProgress({ current: 0, total: 0 });
        }
    };

const handleSave = async () => {
    if (!newProduct.title || !newProduct.priceUSD) return alert("Datos faltantes");
    const client = generateClient();
    setLoading(true);

    try {
        // 1. Limpieza profunda de datos para GraphQL
        const input = {
            title: newProduct.title,
            author: newProduct.author || 'Dr. Giovanni Scarvaci',
            priceUSD: parseFloat(newProduct.priceUSD) || 0,
            pricePhysical: parseFloat(newProduct.pricePhysical) || 0,
            pricePDF: parseFloat(newProduct.pricePDF) || 0,
            priceOnline: parseFloat(newProduct.priceOnline) || 0,
            description: newProduct.description || '',
            category: newProduct.category || 'LIBRO',
            hasDigital: !!newProduct.hasDigital,
            hasOnline: !!newProduct.hasOnline,
            hasPhysical: !!newProduct.hasPhysical,
            imageKey: newProduct.imageKey || '',
            images: Array.isArray(newProduct.images) ? newProduct.images : [],
            pdfKey: newProduct.pdfKey || '',
            onlineExtension: newProduct.onlineExtension || '',
            imagePrefix: newProduct.imagePrefix || '',
            s3FolderName: newProduct.s3FolderName || '',
            totalPages: parseInt(newProduct.totalPages) || 0,
            videos: newProduct.videoKey ? [newProduct.videoKey] : (newProduct.videos || [])
        };

        // 2. Si estamos editando, añadimos el ID obligatorio
        if (editingId) {
            input.id = editingId;
        }

        const mutation = input.category === 'CURSO' 
            ? (editingId ? updateCourse : createCourse) 
            : (editingId ? updateBook : createBook);

        const result = await client.graphql({ 
            query: mutation, 
            variables: { input },
            authMode: 'userPool', 
        });

        if (result.errors) {
            console.error("Errores de validación:", result.errors);
            alert(`Error de AWS: ${result.errors[0].message}`);
        } else {
            alert("✅ GUARDADO EXITOSO EN SCARVACI CLOUD");
            setEditingId(null);
            setNewProduct(initialFormState);
            setView('list');
            fetchInventory();
        }
    } catch (e) { 
        console.error("Error completo:", e);
        const errorMsg = e.errors ? e.errors[0].message : "Error de red o permisos.";
        alert(`No se pudo procesar: ${errorMsg}`);
    } finally {
        setLoading(false);
    }
};

    return (
        <div className="inventory-container">
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h2 style={{ color: '#001f3f' }}><FiPackage /> Panel de Inventario</h2>
                <button className="btn-save-pro" onClick={() => { 
                    if(view === 'create') {
                        setEditingId(null);
                        setNewProduct(initialFormState);
                    }
                    setView(view === 'list' ? 'create' : 'list'); 
                }}>
                    {view === 'list' ? "Agregar al Catálogo" : "Ver Inventario"}
                </button>
            </header>

            {view === 'list' ? (
                <div className="products-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '25px' }}>
                    {products.map(p => (
                        <div key={p.id} className="admin-card-pro" style={{ padding: '20px', textAlign: 'center' }}>
                            <div style={{ height: '180px', background: '#f4f4f4', borderRadius: '10px', overflow: 'hidden', marginBottom: '15px' }}>
                                <img src={p.tempUrl || "https://via.placeholder.com/150"} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="" />
                            </div>
                            <h4>{p.title}</h4>
                            <p style={{fontSize: '12px', color: '#666'}}><FiUser /> {p.author}</p>
                            <p style={{ color: '#B12704', fontSize: '20px', fontWeight: 'bold' }}>${p.priceUSD}</p>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '10px' }}>
                                <button onClick={() => handleEdit(p)} style={{ background: '#00a8e8', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <FiEdit /> Editar
                                </button>
                                <button className="logout-btn" onClick={() => handleDelete(p.id, p.category)}><FiTrash2 /> Eliminar</button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="admin-card-pro fade-in">
                    <h2 className="pro-title">{editingId ? "📝 Editando Obra" : "📖 Publicar Nueva Obra"}</h2>
                    <div className="pro-grid">
                        <div className="pro-column">
                            <label>Título de la Obra</label>
                            <input className="pro-input" value={newProduct.title} onChange={e => setNewProduct({...newProduct, title: e.target.value})} />
                            <label>Autor / Escritor</label>
                            <input className="pro-input" value={newProduct.author} onChange={e => setNewProduct({...newProduct, author: e.target.value})} />
                            <label>Precio Base (USD)</label>
                            <input className="pro-input" type="number" step="0.01" value={newProduct.priceUSD} onChange={e => setNewProduct({...newProduct, priceUSD: e.target.value})} />
                            <div style={{background: '#f0f7f9', padding: '15px', borderRadius: '8px', border: '1px solid #007185', marginTop: '10px'}}>
                                <h4 style={{fontSize: '13px', color: '#007185', marginBottom: '10px'}}>💰 Precios por Modalidad:</h4>
                                <div style={{display: 'grid', gap: '10px'}}>
                                    <input className="pro-input" type="number" step="0.01" placeholder="Precio Físico" value={newProduct.pricePhysical} onChange={e => setNewProduct({...newProduct, pricePhysical: e.target.value})} />
                                    <input className="pro-input" type="number" step="0.01" placeholder="Precio PDF" value={newProduct.pricePDF} onChange={e => setNewProduct({...newProduct, pricePDF: e.target.value})} />
                                    <input className="pro-input" type="number" step="0.01" placeholder="Precio Online" value={newProduct.priceOnline} onChange={e => setNewProduct({...newProduct, priceOnline: e.target.value})} />
                                </div>
                            </div>
                            <label style={{marginTop: '15px'}}>Resumen / Contenido</label>
                            <textarea className="pro-textarea" style={{ height: '120px' }} value={newProduct.description} onChange={e => setNewProduct({...newProduct, description: e.target.value})} />
                        </div>
                        <div className="pro-column">
                            <label>Formatos y Disponibilidad</label>
                            <div className="checkbox-group" style={{ display: 'flex', gap: '15px', padding: '10px', background: '#fff', borderRadius: '8px', border: '1px solid #ddd' }}>
                                <label><input type="checkbox" checked={newProduct.hasDigital} onChange={e => setNewProduct({...newProduct, hasDigital: e.target.checked})} /> PDF</label>
                                <label><input type="checkbox" checked={newProduct.hasOnline} onChange={e => setNewProduct({...newProduct, hasOnline: e.target.checked})} /> Web</label>
                                <label><input type="checkbox" checked={newProduct.hasPhysical} onChange={e => setNewProduct({...newProduct, hasPhysical: e.target.checked})} /> Físico</label>
                            </div>
                            {newProduct.hasOnline && editingId && (
                                <div style={{ marginTop: '15px', padding: '15px', border: '2px solid #D4AF37', borderRadius: '8px', background: '#fff9e6' }}>
                                    <h5 style={{ color: '#856404', marginBottom: '5px' }}>📖 Contenido del Lector Online</h5>
                                    <small style={{display: 'block', marginBottom: '10px'}}>Sube la carpeta con archivos: P-001.png, P-002.png...</small>
                                    <input type="file" webkitdirectory="true" onChange={(e) => handleFolderUpload(e, editingId)} />
                                    {uploadProgress.total > 0 && (
                                        <div style={{ marginTop: '10px' }}>
                                            <div style={{ width: '100%', background: '#ddd', height: '10px', borderRadius: '5px' }}>
                                                <div style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%`, background: '#007185', height: '10px', borderRadius: '5px' }}></div>
                                            </div>
                                            <small>{uploadProgress.current} de {uploadProgress.total} archivos ({newProduct.onlineExtension})</small>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div style={{ marginTop: '20px', background: '#fdfdfd', padding: '20px', borderRadius: '12px', border: '2px dashed #D4AF37' }}>
                                <h4 style={{ marginBottom: '15px' }}>🖼️ Archivos Multimedia</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    <div className="upload-box">
                                        <label><FiImage /> Portada</label>
                                        <input type="file" accept="image/*" onChange={e => handleFileUpload(e, 'imageKey')} />
                                        {newProduct.imageKey && <FiCheckCircle color="green" />}
                                    </div>
                                    <div className="upload-box">
                                        <label><FiVideo /> Video</label>
                                        <input type="file" onChange={e => handleFileUpload(e, 'videoKey')} />
                                        {newProduct.videoKey && <FiCheckCircle color="green" />}
                                    </div>
                                </div>
                                <label style={{ marginTop: '15px', display: 'block' }}>📸 Galería (Múltiple)</label>
                                <input type="file" multiple onChange={e => handleFileUpload(e, 'images', true)} />
                                <label style={{ marginTop: '15px', display: 'block' }}><FiFileText /> PDF del Libro</label>
                                <input type="file" accept="application/pdf" onChange={e => handleFileUpload(e, 'pdfKey')} />
                            </div>
                        </div>
                    </div>
                    <button className="btn-save-pro" style={{ width: '100%', marginTop: '30px' }} onClick={handleSave} disabled={loading}>
                        {loading ? "PROCESANDO..." : editingId ? "ACTUALIZAR EN SCARVACI CLOUD" : "PUBLICAR EN SCARVACI CLOUD"}
                    </button>
                </div>
            )}
        </div>
    );
};

export default InventoryManager;